import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { Alert } from '@mui/material';
import * as z from 'zod';

import Button from '../../common/Button';
import { type TypeDataset } from '../../common/dataTypes';
import { useUpdateDataset } from '../../hooks/tanstack/useDatasetsHook';
import { useGetIFS } from '../../hooks/tanstack/useIFSHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import { handleQueryError } from '../../utils/api';
import FormInput from '../form/FormInput';
import UploadIFSFilesTabs from '../UploadIFSFilesTabs';
import WarningDialog from '../WarningDialog';

const ProjectCreateDatasetSchema = z.object({
  name: z.string().trim().trim().min(1, 'Dataset name is required'),
  description: z.string().trim().optional(),
});

type ProjectCreateProjectSchemaType = z.infer<
  typeof ProjectCreateDatasetSchema
>;

type ProjectCreateDatasetFormProps = {
  handleOnCancel?: () => void;
  handleOnSubmit?: () => void;
  defaultValues?: TypeDataset;
};

const ProjectCreateDatasetForm = ({
  defaultValues,
  handleOnCancel,
  handleOnSubmit,
}: ProjectCreateDatasetFormProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openWarningDialog, setOpenWarningDialog] = useState(false);

  const [error, setError] = useState(Object);

  const { orgId, projectId } = useGetRouteParams();

  const datasetId = Number(defaultValues?.id);

  const step = Number(searchParams.get('step'));

  const form = useForm<ProjectCreateProjectSchemaType>({
    resolver: zodResolver(ProjectCreateDatasetSchema),
    defaultValues,
  });

  const { handleSubmit, reset, formState } = form;

  const { isValid: isFormValid, isDirty } = formState;

  const { data: ifs } = useGetIFS(
    {
      orgId,
      projectId,
      datasetId,
    },
    {
      enabled: !!datasetId && !!projectId && !!orgId,
    }
  );

  const isDisabled = ifs?.files.find((file) => !file.is_imported);

  const isValid = isFormValid && !isDisabled;

  const { mutate: updateDataset, isPending: isUpdatingProject } =
    useUpdateDataset({
      onSuccess: () => {
        handleCloseWarningDialog();
      },
      onError: (error) => {
        handleQueryError(error, setError);
      },
    });

  useEffect(() => {
    if (defaultValues) {
      searchParams.set('dataset_id', defaultValues.id.toString());
      setSearchParams(searchParams);

      reset(defaultValues);
    }
  }, [defaultValues]);

  const handleOpenWarningDialog = () => setOpenWarningDialog(true);

  const handleCloseWarningDialog = () => setOpenWarningDialog(false);

  const handleUpdateDataset = async () => {
    if (!orgId || !projectId) {
      return;
    }

    const data = form.getValues();

    updateDataset({
      orgId,
      projectId,
      datasetId,
      data: {
        ...data,
        project: projectId,
      },
    });
  };

  const onSubmit = () => {
    if (!isFormValid) {
      return;
    }

    if (isDirty) {
      handleOpenWarningDialog();
      return;
    }

    handleOnSubmit?.();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 ">
        <div className="space-y-4">
          <FormInput
            label="Name"
            fullWidth
            inputProps={{
              maxLength: 100,
            }}
            placeholder="Name of dataset"
            name="name"
          />
          <FormInput
            label="Description"
            fullWidth
            multiline
            rows={4}
            placeholder="(Optional)"
            name="description"
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="contained_blue"
            loading={isUpdatingProject}
            disabled={!isDirty || isUpdatingProject}
          >
            Save
          </Button>
        </div>
        {typeof error.detail === 'string' && (
          <Alert severity="error">{error.detail}</Alert>
        )}
        <UploadIFSFilesTabs />
        <div className="flex justify-end">
          <div className="flex gap-3">
            <Button variant="text" onClick={handleOnCancel}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                searchParams.set('step', (step - 1).toString());
                setSearchParams(searchParams);
              }}
              variant="outlined"
            >
              Previous
            </Button>
            <Button variant="outlined" onClick={handleOnCancel}>
              Upload files later
            </Button>
            <Button variant="contained_blue" disabled={!isValid} type="submit">
              Next
            </Button>
          </div>
        </div>
        <WarningDialog
          open={openWarningDialog}
          primaryActionText="Confirm"
          handlePrimaryAction={() => {
            handleUpdateDataset();
          }}
          secondaryActionText="Cancel"
          handleSecondaryAction={handleCloseWarningDialog}
          content={
            <div className="space-y-2">
              <p>
                Saving this dataset will create a new Input File Set and remove
                all previously imported files. This action is irreversible.
              </p>
              <p>Confirm to proceed or cancel to keep your current files.</p>
            </div>
          }
        />
      </form>
    </FormProvider>
  );
};

export default ProjectCreateDatasetForm;
